// SS-4358: the "Upcoming invoice" card at the top of the Invoices page, fed by
// GET /invoices/upcoming. It shows what the next invoice will charge: the plan (charged in
// advance for a prepay practice, at period end otherwise), the usage so far, the discount, the
// prepaid credit as Stripe's preview projects it and the estimated amount due. Hidden when the
// API has nothing to show (data null: a partner-invoiced business, no subscription, or an API
// that does not offer the endpoint yet). There is no PDF: Stripe produces none for a preview.
import React, { useEffect, useState } from "react";
import { Box, Flex, Text, VStack, HStack, Spinner, Badge, Divider } from "@chakra-ui/react";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";

// Stripe's zero-decimal currencies: their amounts are whole units, not hundredths. UGX is
// zero-decimal too, but Stripe's API keeps it two-decimal, so it is not listed.
const ZERO_DECIMAL = new Set(["bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga", "pyg", "rwf", "vnd", "vuv", "xaf", "xof", "xpf"]);

// amounts are in the invoice's currency, in its smallest unit (cents for USD)
export const formatMoney = (cents, currency = "usd") => {
  const value = (Number(cents) || 0) / (ZERO_DECIMAL.has(String(currency || "").toLowerCase()) ? 1 : 100);
  try {
    return value.toLocaleString("en-US", { style: "currency", currency: String(currency || "usd").toUpperCase() });
  } catch (e) {
    return `${value.toFixed(2)} ${String(currency || "").toUpperCase()}`;
  }
};
const day = (seconds) => moment(seconds * 1000).format("MMM D");
const date = (seconds) => moment(seconds * 1000).format("MMM D, YYYY");
const range = (start, end) => `${day(start)} – ${day(end)}`;

const STATE_LABELS = {
  trial: "Trial",
  set_to_cancel: "Set to cancel",
  advance_unpaid: "Advance not yet paid",
  transition: "Billing change",
};

function stateNote(data) {
  switch (data.state) {
    case "trial":
      if (!data.trial_end) return "Your trial is active.";
      if (data.cancel_at && data.cancel_at === data.trial_end) {
        return `Your trial ends on ${date(data.trial_end)}, and your subscription is set to cancel then.`;
      }
      if (data.cancel_at && data.cancel_at < data.trial_end) {
        return `Your subscription is set to cancel on ${date(data.cancel_at)}, before your trial ends on ${date(data.trial_end)}.`;
      }
      if (data.cancel_at) {
        return `Your trial ends on ${date(data.trial_end)}. Your subscription is set to cancel on ${date(data.cancel_at)}.`;
      }
      return data.amount_due > 0
        ? `Your trial ends on ${date(data.trial_end)}; the first charge happens then.`
        : `Your trial ends on ${date(data.trial_end)}.`;
    case "set_to_cancel":
      // the final invoice only when the cancellation comes at (or before) this invoice
      if (data.cancel_at && data.next_invoice_date && data.cancel_at > data.next_invoice_date) {
        return `Your subscription is set to cancel on ${date(data.cancel_at)}. Until then your plan is billed at the end of each period.`;
      }
      return "Your subscription is set to cancel. This is your final invoice: it carries no plan charged in advance.";
    case "advance_unpaid":
      return "The plan charged in advance for this period has not been paid yet.";
    case "transition":
      return "This invoice charges the plan for the period now ending at its end, plus the next period's plan in advance. After that, your plan appears once per invoice.";
    default:
      return null;
  }
}

// a 404 (an API without the endpoint answers Express's HTML page, which cqAPI cannot parse)
const endpointMissing = (response) => response?.code === 404 || response?.msg === "Unable to parse response";

// how much of this period's plan the prepaid credit covers, in words
function coverage(data) {
  const plan = data.current_plan_at_end?.amount || 0;
  if (data.state === "transition" || !(data.credit_amount > 0)) return "billed at its end";
  // the credit is the plan net of its coupon (net_amount); the invoice's discount can also cover usage
  const net = data.current_plan_at_end?.net_amount ?? plan;
  return data.credit_amount >= net ? "covered by your prepaid credit" : "partly covered by your prepaid credit";
}

// amount: the row's signed contribution to the amount due (data-amount, so every row is checkable)
function Row({ label, value, testId, bold, amount }) {
  return (
    <Flex justify="space-between" fontSize="sm" data-testid={testId} data-amount={amount}>
      <Text fontWeight={bold ? "bold" : "normal"}>{label}</Text>
      <Text fontWeight={bold ? "bold" : "normal"}>{value}</Text>
    </Flex>
  );
}

function UpcomingInvoice() {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    cqAPI
      .get(`/invoices/upcoming`)
      .then((response) => {
        if (cancelled) return;
        if (response?.type === "ok") {
          setData(response.data || null);
          setStatus("ok");
        } else if (endpointMissing(response)) {
          // an API that does not offer the endpoint yet: no card, as before
          setData(null);
          setStatus("ok");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") {
    return (
      <Box borderWidth="1px" borderRadius="md" p={4} data-testid="upcoming-invoice-loading">
        <HStack>
          <Spinner size="sm" />
          <Text fontSize="sm">Loading your upcoming invoice…</Text>
        </HStack>
      </Box>
    );
  }
  if (status === "error") {
    return (
      <Box borderWidth="1px" borderRadius="md" p={4} data-testid="upcoming-invoice-error">
        <Text fontSize="sm" color="red.500">
          Your upcoming invoice could not be loaded. Please try again later.
        </Text>
      </Box>
    );
  }
  if (!data) {
    return null;
  }

  const note = stateNote(data);
  const plan = data.plan;
  const money = (cents) => formatMoney(cents, data.currency);
  return (
    <Box borderWidth="1px" borderRadius="md" p={4} data-testid="upcoming-invoice">
      <VStack align="stretch" spacing={2}>
        <Flex justify="space-between" align="center">
          <Text fontWeight="bold">Upcoming invoice</Text>
          <HStack>
            {STATE_LABELS[data.state] && <Badge data-testid="upcoming-invoice-state">{STATE_LABELS[data.state]}</Badge>}
            <Text fontSize="sm" data-testid="upcoming-invoice-date">
              {data.next_invoice_date ? date(data.next_invoice_date) : ""}
            </Text>
          </HStack>
        </Flex>
        {note && (
          <Text fontSize="sm" color="gray.500" data-testid="upcoming-invoice-note">
            {note}
          </Text>
        )}
        {data.current_plan_at_end && (
          <Row
            testId="upcoming-invoice-current-plan"
            label={`Plan for ${range(data.current_plan_at_end.period_start, data.current_plan_at_end.period_end)} (${coverage(data)})`}
            value={money(data.current_plan_at_end.amount)}
            amount={data.current_plan_at_end.amount}
          />
        )}
        {plan && plan.charged === "in_advance" && (
          <Row
            testId="upcoming-invoice-plan"
            label={`Plan for ${range(plan.period_start, plan.period_end)}, charged in advance on ${date(plan.charged_on)}`}
            value={money(plan.amount)}
            amount={plan.amount}
          />
        )}
        {plan && plan.charged === "at_period_end" && plan.amount !== null && plan.amount !== undefined && (
          <Row
            testId="upcoming-invoice-plan"
            label={`Plan for ${range(plan.period_start, plan.period_end)}, billed at period end`}
            value={money(plan.amount)}
            amount={plan.amount}
          />
        )}
        {(data.usage || []).map((usage) => (
          <Box key={usage.subscription_item} data-testid="upcoming-invoice-usage">
            <Row
              label={`${usage.name}: ${Number(usage.quantity || 0).toLocaleString("en-US")} used${
                usage.included !== null && usage.included !== undefined
                  ? ` of ${Number(usage.included).toLocaleString("en-US")} included`
                  : ""
              }`}
              value={`${money(usage.overage_amount)} over plan so far`}
              amount={usage.overage_amount || 0}
            />
          </Box>
        ))}
        {Number(data.other_charges) !== 0 && data.other_charges !== null && data.other_charges !== undefined && (
          <Row testId="upcoming-invoice-other" label="Other charges and adjustments (add-ons, other usage)" value={money(data.other_charges)} amount={data.other_charges} />
        )}
        {data.usage_as_of && (
          <Text fontSize="xs" color="gray.500" data-testid="upcoming-invoice-as-of">
            Usage as of {moment(data.usage_as_of * 1000).format("MMM D, YYYY h:mm A")} (it can take a few minutes to appear)
          </Text>
        )}
        <Divider />
        {Number(data.tax_amount) !== 0 && data.tax_amount !== null && data.tax_amount !== undefined && (
          <Row testId="upcoming-invoice-tax" label="Tax" value={money(data.tax_amount)} amount={data.tax_amount} />
        )}
        {data.discount_amount > 0 && (
          <Row testId="upcoming-invoice-discount" label="Discount" value={money(-data.discount_amount)} amount={-data.discount_amount} />
        )}
        {data.credit_amount > 0 && (
          <Box data-testid="upcoming-invoice-credit">
            <Row label="Prepaid plan credit" value={money(-data.credit_amount)} amount={-data.credit_amount} />
            {(data.credit_sources || []).map((source) => (
              <Text fontSize="xs" color="gray.500" key={source.invoice_id}>
                From invoice {source.number || source.invoice_id}
                {source.paid_at ? `, paid ${date(source.paid_at)}` : ""}
              </Text>
            ))}
          </Box>
        )}
        {data.balance_applied > 0 && (
          <Row testId="upcoming-invoice-balance" label="Account credit" value={money(-data.balance_applied)} amount={-data.balance_applied} />
        )}
        <Flex justify="space-between" fontSize="sm" data-testid="upcoming-invoice-amount-due" data-total={data.amount_due}>
          <Text fontWeight="bold">Estimated amount due</Text>
          <Text fontWeight="bold">{money(data.amount_due)}</Text>
        </Flex>
      </VStack>
    </Box>
  );
}

export default UpcomingInvoice;
